import { FormikProvider } from 'formik';
import styled from 'styled-components';

import { Button } from '../../cdl/Button/Button';
import { CheckboxGroup } from '../../cdl/CheckboxGroup/CheckboxGroup';
import { FormikInput } from '../../cdl/Input/FormikInput';
import { Label, Legend } from '../../cdl/Label/Label';
import { Modal } from '../../cdl/Modal/Modal';
import { Radio, RadioGroup, RadioGroupItem } from '../../cdl/RadioGroup/RadioGroup';
import { ReadOnlyField } from '../../cdl/ReadOnlyField/ReadOnlyField';
import { Text } from '../../cdl/Text/Text';
import { translate } from '../helpers/translate.helper';
import { useEnums } from '../hooks/useEnums';
import { Box } from '../ui/Box';
import { theme } from '../ui/theme';

import { REQUISITION_GROUP_SECTIONS, REQUISITION_GROUP_PRESETS } from './requisitionGroups';
import { RequisitionAction } from './types/RequisitionAction';
import { RequisitionGroupSelection } from './types/RequisitionGroupSelection';
import { RequisitionProductSource } from './types/RequisitionProductSource';
import { RequisitionScope } from './types/RequisitionScope';
import { useVesselRequisitionForm, VesselRequisitionFormProps } from './useVesselRequisitionForm';

const CustomGroups = styled.div<{ $expanded: boolean }>`
    display: grid;
    grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    visibility: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
    margin-bottom: ${({ $expanded }) => ($expanded ? theme.space[0] : `-${theme.space[12]}`)};
    transition:
        grid-template-rows 180ms ease-out,
        opacity 180ms ease-out,
        margin-bottom 180ms ease-out,
        visibility 180ms;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

export const VesselRequisitionModal = ({
    scope,
    vesselEmailAddress,
    onDismiss,
    onSubmit,
}: VesselRequisitionFormProps) => {
    const { getHumanReadableValue } = useEnums();
    const { formik, submit, action } = useVesselRequisitionForm({ scope, vesselEmailAddress, onDismiss, onSubmit });

    return (
        <FormikProvider value={formik}>
            <Modal isOpen onDismiss={onDismiss}>
                <Box
                    as="form"
                    noValidate
                    padding={24}
                    display="flex"
                    flexDirection="column"
                    gap={24}
                    onSubmit={(event) => {
                        event.preventDefault();
                        void submit(RequisitionAction.DOWNLOAD);
                    }}
                >
                    <Box>
                        <Text as="h2" variant="title">
                            {translate(
                                scope === RequisitionScope.COMPANY
                                    ? 'requisition.customize.companyTitle'
                                    : 'requisition.customize.title'
                            )}
                        </Text>
                        {scope === RequisitionScope.COMPANY ? (
                            <Text as="p" variant="body" color="foreground.subtle">
                                {translate('requisition.customize.companyDescription')}
                            </Text>
                        ) : null}
                    </Box>
                    <Box display="flex" flexDirection="column" gap={12}>
                        <RadioGroup
                            value={formik.values.productSource}
                            onValueChange={(value) => formik.setFieldValue('productSource', value)}
                        >
                            <Legend>{translate('requisition.customize.products')}</Legend>
                            <RadioGroupItem>
                                <Radio value={RequisitionProductSource.CATALOGUE} />
                                <Label>{translate('requisition.customize.catalogue')}</Label>
                            </RadioGroupItem>
                            <RadioGroupItem>
                                <Radio value={RequisitionProductSource.VESSEL_PRODUCTS} />
                                <Label>{translate('requisition.customize.vesselProducts')}</Label>
                            </RadioGroupItem>
                        </RadioGroup>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={12}>
                        <RadioGroup
                            orientation="vertical"
                            value={formik.values.groupSelection}
                            onValueChange={(value: RequisitionGroupSelection) =>
                                formik.setValues({
                                    ...formik.values,
                                    groupSelection: value,
                                    groups: REQUISITION_GROUP_PRESETS[value],
                                })
                            }
                        >
                            <Legend>{translate('requisition.customize.groups')}</Legend>
                            <Box display="flex" flexDirection="column" gap={4}>
                                {Object.values(RequisitionGroupSelection).map((selection) => (
                                    <RadioGroupItem key={selection}>
                                        <Radio value={selection} />
                                        <Label>{translate(`requisition.customize.${selection}`)}</Label>
                                    </RadioGroupItem>
                                ))}
                            </Box>
                        </RadioGroup>
                        <CustomGroups
                            $expanded={formik.values.groupSelection === RequisitionGroupSelection.CUSTOM}
                            aria-hidden={formik.values.groupSelection !== RequisitionGroupSelection.CUSTOM}
                        >
                            <Box minHeight={0} overflow="hidden">
                                <Box paddingLeft={28} display="flex" flexDirection="column">
                                    {REQUISITION_GROUP_SECTIONS.map((section) => (
                                        <CheckboxGroup
                                            key={section[0]}
                                            options={section.map((group) => ({
                                                value: group,
                                                label: (
                                                    <>
                                                        {getHumanReadableValue('productGroup', group)}
                                                        {REQUISITION_GROUP_PRESETS[
                                                            RequisitionGroupSelection.MAIN
                                                        ].includes(group) ? (
                                                            <Text
                                                                variant="small"
                                                                color="foreground.subtle"
                                                                marginLeft={4}
                                                            >
                                                                {translate('requisition.customize.mainClassification')}
                                                            </Text>
                                                        ) : null}
                                                        {REQUISITION_GROUP_PRESETS[
                                                            RequisitionGroupSelection.MINOR
                                                        ].includes(group) ? (
                                                            <Text
                                                                variant="small"
                                                                color="foreground.subtle"
                                                                marginLeft={4}
                                                            >
                                                                {translate('requisition.customize.minorClassification')}
                                                            </Text>
                                                        ) : null}
                                                    </>
                                                ),
                                            }))}
                                            value={formik.values.groups.filter((group) => section.includes(group))}
                                            onChange={(selected) =>
                                                formik.setFieldValue('groups', [
                                                    ...formik.values.groups.filter((group) => !section.includes(group)),
                                                    ...selected,
                                                ])
                                            }
                                            disabled={formik.values.groupSelection !== RequisitionGroupSelection.CUSTOM}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </CustomGroups>
                        {formik.values.groups.length === 0 ? (
                            <Text variant="extraSmall">{translate('requisition.customize.selectGroup')}</Text>
                        ) : null}
                    </Box>
                    {scope === RequisitionScope.COMPANY ? (
                        <Text variant="extraSmall" color="foreground.subtle">
                            {translate('requisition.customize.companyRecipients')}
                        </Text>
                    ) : null}
                    {scope === RequisitionScope.VESSEL ? (
                        <Box display="flex" flexDirection="column" gap={12}>
                            {vesselEmailAddress?.trim() ? (
                                <ReadOnlyField
                                    label={translate('requisition.customize.vesselEmail')}
                                    value={vesselEmailAddress}
                                />
                            ) : (
                                <FormikInput
                                    type="email"
                                    label={translate('requisition.customize.vesselEmail')}
                                    name="emailAddress"
                                    autoComplete="email"
                                    caption={translate('requisition.customize.noSavedEmail')}
                                />
                            )}
                        </Box>
                    ) : null}
                    {formik.status ? <Text variant="body">{formik.status}</Text> : null}
                    <Box display="flex" justifyContent="end" gap={8}>
                        <Button type="button" emphasis="low" onClick={onDismiss}>
                            {translate('btn.cancel')}
                        </Button>
                        <Button
                            type="button"
                            emphasis="medium"
                            isLoading={formik.isSubmitting && action === RequisitionAction.SEND}
                            disabled={formik.values.groups.length === 0}
                            onClick={() => void submit(RequisitionAction.SEND)}
                        >
                            {translate(
                                scope === RequisitionScope.COMPANY
                                    ? 'requisition.customize.sendToVessels'
                                    : 'vessel.requisitionForm.sendToVesselButton'
                            )}
                        </Button>
                        <Button
                            type="submit"
                            emphasis="high"
                            isLoading={formik.isSubmitting && action === RequisitionAction.DOWNLOAD}
                            disabled={formik.values.groups.length === 0}
                        >
                            {translate('vessel.requisitionForm.downloadButton')}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </FormikProvider>
    );
};
